// useStepDrag — the drag SESSION for structured step reorder (plan §2/§5 P2).
// Owns the "grab → track the pointer → drop" lifecycle and delegates the
// geometry math to the pure `resolveVerticalDrop`. Kept surface-agnostic: the
// view injects the sibling bands, the clientY→band-space projection, and the
// commit (`moveStep`), so the whole interaction is unit-testable with synthetic
// pointer events and reused by every drag surface. Text stays truth — the drop
// funnels through moveStep → the mutation funnel; nothing is optimistic.
//
// Two entry points share ONE session + resolve/commit machinery:
//   - `onHandleDown` — an explicit grip (the ⋮⋮ handle): drag starts immediately.
//   - `onBodyDown` — the ROW/CELL body: threshold-gated (P2 #19, "drag by body").
//     Below `DRAG_THRESHOLD_PX` of travel the press is left ALONE — the element's
//     own `onClick` fires as usual (select). Past the threshold it becomes a drag
//     AND swallows the click that native pointerup would otherwise synthesize
//     (a completed drag must not also select). So the body is both the click
//     target and the drag grip, with no double-handling.

import { useCallback, useEffect, useRef, useState } from "react";
import type { DragRow, DropTarget, BlockZone } from "./processDragResolve.js";

/** A minimal pointer-ish event — only what the handle-down needs (so callers can
 *  pass a React or DOM event, and tests a bare stub). */
export interface HandleDownEvent {
  readonly clientY: number;
  preventDefault(): void;
  stopPropagation(): void;
}

/** A body-down event — carries both axes (the threshold is 2-D). A React or DOM
 *  MouseEvent satisfies it; tests pass a stub. */
export interface BodyDownEvent {
  readonly clientX: number;
  readonly clientY: number;
}

/** Pointer travel (px, either axis) that promotes a body press into a drag. Below
 *  it, the press is left to the element's own click. Matches the usual OS slop. */
export const DRAG_THRESHOLD_PX = 4;

/** Where a live drag will land: a sibling reorder OR a reparent into a block. */
export type StepDropTarget =
  | { readonly kind: "reorder"; readonly anchorNodeId: string; readonly position: "before" | "after" }
  | { readonly kind: "into"; readonly blockNodeId: string };

export interface StepDragApi {
  /** The nodeId currently being dragged, or null when idle. */
  readonly draggingNodeId: string | null;
  /** The live drop target (drives the insertion line / block highlight), or null. */
  readonly target: StepDropTarget | null;
  /** Begin a drag from a step's explicit handle (immediate). */
  readonly onHandleDown: (nodeId: string, e: HandleDownEvent) => void;
  /** Arm a threshold-gated drag from the row/cell BODY. Does NOT preventDefault
   *  or own selection — the element keeps its own `onClick`. A sub-threshold
   *  release leaves that click intact; a real drag swallows the trailing click. */
  readonly onBodyDown: (nodeId: string, e: BodyDownEvent) => void;
}

export interface StepDragOptions {
  /** Resolve the drop target for the current pointer (view owns the geometry —
   *  block-into first, then sibling reorder). Called on each pointer move. */
  readonly resolve: (clientX: number, clientY: number, draggedNodeId: string) => StepDropTarget | null;
  /** Commit the resolved target (→ moveStepInto for `into`, moveStep for
   *  `reorder`). Only called on drop when a target is live. */
  readonly commit: (draggedNodeId: string, target: StepDropTarget) => void;
}

/** Swallow exactly ONE upcoming `click` (capture phase) — used after a real
 *  drag so the pointerup-synthesized click doesn't also select. Self-removes. */
function swallowNextClick(): void {
  const onClick = (ev: Event) => {
    ev.stopPropagation();
    ev.preventDefault();
    window.removeEventListener("click", onClick, true);
  };
  window.addEventListener("click", onClick, true);
  // Safety: if no click follows (some environments), drop the trap next tick so
  // it never eats an unrelated later click.
  setTimeout(() => window.removeEventListener("click", onClick, true), 0);
}

export function useStepDrag(opts: StepDragOptions): StepDragApi {
  const [draggingNodeId, setDragging] = useState<string | null>(null);
  const [target, setTarget] = useState<StepDropTarget | null>(null);
  // Latest opts + live drag target, read inside the window listeners without
  // re-subscribing every render.
  const optsRef = useRef(opts);
  optsRef.current = opts;
  const targetRef = useRef<StepDropTarget | null>(null);

  const beginDrag = useCallback((nodeId: string) => {
    targetRef.current = null;
    setTarget(null);
    setDragging(nodeId);
  }, []);

  const onHandleDown = useCallback((nodeId: string, e: HandleDownEvent) => {
    e.preventDefault();
    e.stopPropagation();
    beginDrag(nodeId);
  }, [beginDrag]);

  // Threshold-gated body press. The arming listeners live here (not in the
  // active-drag effect) so a sub-threshold release stays a pure click and never
  // touches the model. Registered imperatively on arm, torn down on
  // promote/release/cancel — a fresh set per press.
  const onBodyDown = useCallback(
    (nodeId: string, e: BodyDownEvent) => {
      const startX = e.clientX;
      const startY = e.clientY;
      let armed = true;
      const teardown = () => {
        armed = false;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("keydown", onKey);
      };
      const onMove = (ev: Event) => {
        if (!armed) return;
        const m = ev as MouseEvent;
        if (Math.abs(m.clientX - startX) > DRAG_THRESHOLD_PX || Math.abs(m.clientY - startY) > DRAG_THRESHOLD_PX) {
          teardown();
          swallowNextClick(); // a real drag — don't let its trailing click select
          beginDrag(nodeId); // the active-drag effect takes over from here
        }
      };
      const onUp = () => teardown(); // sub-threshold release → leave the native click
      const onKey = (ev: KeyboardEvent) => {
        if (ev.key === "Escape") teardown();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("keydown", onKey);
    },
    [beginDrag],
  );

  useEffect(() => {
    if (!draggingNodeId) return;
    const move = (ev: Event) => {
      const m = ev as MouseEvent;
      const t = optsRef.current.resolve(m.clientX, m.clientY, draggingNodeId);
      targetRef.current = t;
      setTarget(t);
    };
    const up = () => {
      const t = targetRef.current;
      if (t) optsRef.current.commit(draggingNodeId, t);
      targetRef.current = null;
      setTarget(null);
      setDragging(null);
    };
    const cancel = () => {
      targetRef.current = null;
      setTarget(null);
      setDragging(null);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    // Escape abandons the drag without moving (mirrors the inline-edit cancels).
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") cancel();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("keydown", onKey);
    };
  }, [draggingNodeId]);

  return { draggingNodeId, target, onHandleDown, onBodyDown };
}

export type { DragRow, DropTarget, BlockZone } from "./processDragResolve.js";
